// src/pages/GuidePage.js
// Reusable component to render any immigration guide from data objects

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import allGuides from "./immigrationGuides/index";
import useDocumentMeta from "../useDocumentMeta";
import SolicitorModal from "../SolicitorModal";
import { safeGetItem, safeSetItem } from "../safeStorage";

const CHECKLIST_STORAGE_KEY = "lifeInUkGuideChecklists";

// Guide content is plain text with light markdown (**bold** and "- " list
// items) rather than HTML, so it can be authored as simple strings in data
// files. This turns that into real JSX instead of rendering literal
// asterisks/dashes.
function renderInlineMarkdown(text, keyPrefix) {
    // Link labels legitimately contain parentheses (e.g. "[settle in the UK
    // (Indefinite Leave to Remain)](url)"). A plain [^)]+ URL match stops at the
    // label's first ")", truncating the href so the whole link falls through to
    // literal text. Allow one level of balanced parens in both label and URL.
    const parts = text.split(/(\*\*[^*]+\*\*|\[(?:[^[\]]|\([^)]*\))+\]\((?:[^()\s]|\([^)]*\))+\))/g);
    return parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            // Recurse: a link can sit inside bold ("**Pass an approved
            // [SELT](url)**"), and rendering the inner text raw would print the
            // markdown source.
            return (
                <strong key={`${keyPrefix}-${i}`}>
                    {renderInlineMarkdown(part.slice(2, -2), `${keyPrefix}-${i}b`)}
                </strong>
            );
        }
        const linkMatch = part.match(/^\[((?:[^[\]]|\([^)]*\))+)\]\(((?:[^()\s]|\([^)]*\))+)\)$/);
        if (linkMatch) {
            const [, label, url] = linkMatch;
            const isExternal = /^https?:\/\//.test(url);
            return (
                <a
                    key={`${keyPrefix}-${i}`}
                    href={url}
                    className="text-action-text hover:opacity-80 underline py-3 inline-flex items-center"
                    {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                    {label}
                </a>
            );
        }
        return part;
    });
}

// Guide content in the data files is hard-wrapped at ~75 characters for
// readability in source. Those wraps are NOT paragraph breaks — paragraphs are
// separated by a blank line. Treating every source line as its own block (as
// this used to) produced two visible bugs: a gap after every wrapped line, and
// markdown links broken across a wrap (`[label text\nrest](url)`) matching
// neither half of the inline link regex, so they rendered as literal
// "[label](url)" text. 15 links across the 14 immigration guides were affected.
// So: accumulate consecutive non-blank lines into one paragraph, and only break
// on a blank line. Wrapped list-item text likewise folds into the item above.
// Plain-text preview of guide content for teaser snippets. These render inside
// a <Link>, so real anchors cannot be nested there — the markdown has to be
// flattened to its label text rather than turned into elements. Strips the
// wrapping first, THEN truncates, so a cut mid-"](url)" can't leak syntax.
function toPlainText(markdown, maxLength) {
    const plain = (markdown || "")
        .replace(/\[((?:[^[\]]|\([^)]*\))+)\]\((?:[^()\s]|\([^)]*\))+\)/gs, "$1")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\s+/g, " ")
        .trim();
    if (!maxLength || plain.length <= maxLength) return plain;
    return `${plain.slice(0, maxLength).trimEnd()}…`;
}

function renderGuideContent(content) {
    const lines = content.split("\n");
    const blocks = [];
    let currentList = null;
    let currentParagraph = null;

    const flushParagraph = () => {
        if (currentParagraph) {
            blocks.push({ type: "paragraph", text: currentParagraph.join(" ") });
            currentParagraph = null;
        }
    };

    lines.forEach((line) => {
        const trimmed = line.trim();
        const listMatch = trimmed.match(/^-\s+(.*)$/);

        if (listMatch) {
            flushParagraph();
            if (!currentList) {
                currentList = [];
                blocks.push({ type: "list", items: currentList });
            }
            currentList.push(listMatch[1]);
        } else if (trimmed.length === 0) {
            // Blank line: the only real block separator.
            flushParagraph();
            currentList = null;
        } else if (currentList) {
            // Continuation of the previous list item's wrapped text.
            currentList[currentList.length - 1] += ` ${trimmed}`;
        } else {
            (currentParagraph ||= []).push(trimmed);
        }
    });

    flushParagraph();

    return blocks.map((block, i) => {
        if (block.type === "list") {
            return (
                <ul key={i} className="list-disc pl-5 space-y-1.5 mb-3">
                    {block.items.map((item, j) => (
                        <li key={j}>{renderInlineMarkdown(item, `${i}-${j}`)}</li>
                    ))}
                </ul>
            );
        }
        return (
            <p key={i} className="mb-3 last:mb-0">
                {renderInlineMarkdown(block.text, `${i}`)}
            </p>
        );
    });
}

export default function GuidePage({ guide }) {
    useDocumentMeta({
        title: guide?.seo?.metaTitle,
        description: guide?.seo?.metaDescription,
        path: guide ? `/${guide.slug}` : undefined,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [guide]);

    const [showSolicitorForm, setShowSolicitorForm] = useState(false);

    // Persisted checklist progress, keyed by guide slug so each guide's
    // checklist state is tracked independently.
    const [checkedItems, setCheckedItems] = useState(() => {
        const allChecklists = safeGetItem(CHECKLIST_STORAGE_KEY, {});
        return (guide && allChecklists[guide.slug]) || {};
    });

    useEffect(() => {
        const allChecklists = safeGetItem(CHECKLIST_STORAGE_KEY, {});
        setCheckedItems((guide && allChecklists[guide.slug]) || {});
    }, [guide]);

    const toggleChecklistItem = (index) => {
        if (!guide) return;
        const newChecked = { ...checkedItems, [index]: !checkedItems[index] };
        setCheckedItems(newChecked);
        const allChecklists = safeGetItem(CHECKLIST_STORAGE_KEY, {});
        allChecklists[guide.slug] = newChecked;
        safeSetItem(CHECKLIST_STORAGE_KEY, allChecklists);
    };

    if (!guide) {
        return (
            <div className="text-center py-20">
                <div className="text-6xl mb-4">📄</div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Guide Not Found</h2>
                <p className="text-slate-500 mb-6">The page you're looking for doesn't exist.</p>
                <Link to="/" className="text-indigo-600 hover:text-indigo-800 underline font-medium">
                    ← Back to Home
                </Link>
            </div>
        );
    }

    // Generate FAQ JSON-LD schema
    const faqSchema = guide.faqs && guide.faqs.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": guide.faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        }
        : null;

    // Generate Breadcrumb JSON-LD schema
    const breadcrumbSchema = guide.breadcrumbs
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": guide.breadcrumbs.map((crumb, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "name": crumb.name,
                // Trailing slash to match what Netlify actually serves: the
                // slashless form 301s, and naming a redirect in BreadcrumbList
                // is the same mistake the canonical tag made (see
                // useDocumentMeta.js).
                "item": `https://lifeinukcoach.co.uk${crumb.url === "/" ? "/" : crumb.url.replace(/\/?$/, "/")}`
            }))
        }
        : null;

    return (
        <div className="max-w-4xl mx-auto">
            {/* JSON-LD Schemas - only render when data exists */}
            {faqSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqSchema)
                    }}
                />
            )}
            {breadcrumbSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(breadcrumbSchema)
                    }}
                />
            )}

            {/* Breadcrumbs */}
            {guide.breadcrumbs && (
                <nav className="text-sm text-ink-500 mb-6 flex flex-wrap items-center gap-1">
                    {guide.breadcrumbs.map((crumb, i) => (
                        <span key={i} className="flex items-center gap-1">
                            {i > 0 && <span className="mx-1">›</span>}
                            {i < guide.breadcrumbs.length - 1 ? (
                                <Link to={crumb.url} className="text-action-text hover:opacity-80 underline py-3 inline-flex items-center">
                                    {crumb.name}
                                </Link>
                            ) : (
                                <span className="text-ink-700 font-medium py-3 inline-flex items-center">{crumb.name}</span>
                            )}
                        </span>
                    ))}
                </nav>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-extrabold text-ink-900 mb-2">
                {guide.title}
            </h1>
            {guide.metadata?.lastUpdated && (
                <p className="text-xs text-slate-400 mb-4 flex items-center gap-1">
                    <span>✓</span> Last verified: {guide.metadata.lastUpdated}
                </p>
            )}

            {/* Introduction */}
            {guide.introduction && (
                <div className="bg-action-50 border border-action-100 rounded-xl p-5 mb-8">
                    <div className="text-slate-700 leading-relaxed">
                        {renderGuideContent(guide.introduction)}
                    </div>
                </div>
            )}

            {/* Sections */}
            <div className="space-y-8 mb-10">
                {guide.sections.map((section, i) => (
                    <div key={i} className="bg-ink-50 rounded-2xl border border-ink-200 p-6 shadow-lift-1">
                        <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
                            <span className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                                {i + 1}
                            </span>
                            {section.heading}
                        </h2>
                        <div className="text-slate-600 leading-relaxed">
                            {renderGuideContent(section.content)}
                        </div>
                        {section.solicitorCta && (
                            <button
                                type="button"
                                onClick={() => setShowSolicitorForm(true)}
                                className="mt-4 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition shadow-sm"
                            >
                                Talk to an Immigration Solicitor →
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Checklist (click an item to mark it done — saved on this device) */}
            {guide.checklist && guide.checklist.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-10">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold text-emerald-800">✅ Checklist</h3>
                        <span className="text-xs font-semibold text-emerald-600">
                            {Object.values(checkedItems).filter(Boolean).length} / {guide.checklist.length} done
                        </span>
                    </div>
                    <ul className="space-y-2">
                        {guide.checklist.map((item, i) => {
                            const isChecked = !!checkedItems[i];
                            return (
                                <li key={i}>
                                    <button
                                        type="button"
                                        onClick={() => toggleChecklistItem(i)}
                                        className={`w-full flex items-start gap-2 text-left transition ${isChecked ? "text-emerald-400 line-through" : "text-emerald-700 hover:text-emerald-900"}`}
                                    >
                                        <span className="mt-0.5 shrink-0">{isChecked ? "☑" : "☐"}</span>
                                        <span>{item}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* FAQs */}
            {guide.faqs && guide.faqs.length > 0 && (
                <div className="mb-10">
                    <h3 className="text-2xl font-bold text-slate-800 mb-4">❓ Frequently Asked Questions</h3>
                    <div className="space-y-3">
                        {guide.faqs.map((faq, i) => (
                            <details key={i} className="bg-ink-50 rounded-xl border border-ink-200 shadow-lift-1 overflow-hidden">
                                <summary className="px-5 py-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-50 transition list-none flex items-center justify-between">
                                    <span>{faq.question}</span>
                                    <span className="text-indigo-400 text-xl leading-none">▾</span>
                                </summary>
                                <div className="px-5 pb-4 text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                                    {faq.answer}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            )}

            {/* Suggested next step in the journey */}
            {guide.relatedPages && guide.relatedPages.length > 0 && (
                <Link
                    to={guide.relatedPages[0].url}
                    className="block bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl p-6 mb-10 transition shadow-md"
                >
                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-200 mb-1">Suggested next step</p>
                    <div className="flex items-center justify-between">
                        <span className="text-lg font-bold">{guide.relatedPages[0].title}</span>
                        <span className="text-2xl">→</span>
                    </div>
                </Link>
            )}

            {/* Related Pages */}
            {guide.relatedPages && guide.relatedPages.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6 mb-10 border border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 mb-3">📖 Related Guides</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {guide.relatedPages.map((page, i) => (
                            <Link
                                key={i}
                                to={page.url}
                                className="flex items-center gap-2 p-3 min-h-tap bg-ink-50 rounded-xl border border-ink-200 hover:border-action-500 hover:shadow-lift-1 transition"
                            >
                                <span className="text-indigo-500">→</span>
                                <span className="text-sm font-medium text-slate-700">{page.title}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Official Sources */}
            {guide.officialSources && guide.officialSources.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10">
                    <h3 className="text-lg font-bold text-amber-800 mb-3">🔗 Official Sources</h3>
                    <ul className="space-y-2">
                        {guide.officialSources.map((source, i) => (
                            <li key={i}>
                                <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-amber-700 hover:text-amber-900 underline text-sm"
                                >
                                    <span>📄</span>
                                    {source.title}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Metadata footer */}
            {guide.metadata && (
                <div className="text-center text-xs text-slate-400 border-t border-slate-100 pt-6 mt-6">
                    <p>
                        Last updated: {guide.metadata.lastUpdated} • Category: {guide.metadata.category}
                    </p>
                </div>
            )}

            {/* All Immigration Guides - discoverability section */}
            <div className="bg-ink-50 rounded-2xl border border-ink-200 p-6 shadow-lift-1 mb-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-4">📖 All Immigration & Citizenship Guides</h2>
                <p className="text-slate-600 mb-4">Browse our complete library of guides to help you through every step of your UK immigration journey:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allGuides.filter(g => g.slug !== guide.slug).map((g) => (
                        <Link
                            key={g.slug}
                            to={g.slug === "ilr-guide" ? "/ilr-guide" : `/${g.slug}`}
                            className="block bg-ink-100 border border-ink-200 rounded-xl p-4 hover:border-action-500 hover:shadow-lift-2 transition"
                        >
                            <h3 className="font-bold text-slate-800 text-sm">{g.title}</h3>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{toPlainText(g.introduction, 100)}</p>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Solicitor CTA */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-8 text-center">
                <h3 className="text-xl font-bold text-indigo-800 mb-2">Need further assistance?</h3>
                <p className="text-indigo-700 mb-4">If you need personalized advice on your immigration journey, we can connect you with trusted solicitors.</p>
                <button
                    onClick={() => setShowSolicitorForm(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-xl font-bold transition shadow-md"
                >
                    Find an Immigration Solicitor
                </button>
            </div>
            <SolicitorModal isOpen={showSolicitorForm} onClose={() => setShowSolicitorForm(false)} />

            {/* Back to home */}
            <div className="text-center mt-8 mb-12">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition"
                >
                    ← Back to Home
                </Link>
            </div>
        </div>
    );
}
